import { CalendarEvent, TaskItem } from '@demind-inc/core';
import { DragFromOutsideItemArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import { useDrop } from 'react-dnd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { convertTaskToEvent, getDueDateYYYYMMDD, getIsOverdue } from '../helpers';
import { isAppleRemindersDesktopSupported } from '../helpers/appleRemindersDesktop.helpers';
import {
  DEFAULT_TASK_DURATION_MIN,
  useAuthContext,
  useCalendarContext,
  useCircadianContext,
  eventsSnackBarAtom,
  isAssignedTeamTask,
  useEventActions,
} from '../data-access';
import { getCssVariable, trackEventMixpanel } from '../utils';

const primaryColor = getCssVariable('--color-primary');

export const useDnDTaskToCalendar = () => {
  const [draggedNewEvent, setDraggedNewEvent] = useState<CalendarEvent>();
  const [draggedTask, setDraggedTask] = useState<TaskItem>();
  const [, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);

  const { createCalendarEvent } = useEventActions();
  const { user } = useAuthContext();
  const { mainCalendar, calendarPreferences } = useCalendarContext();
  const { findPhaseForTaskTime } = useCircadianContext();
  const draggedTaskRef = useRef<TaskItem>();
  const shouldBlockAppleReminderDnD = useCallback(
    (task?: TaskItem) =>
      task?.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported(),
    []
  );
  const shouldBlockTaskDnD = useCallback(
    (task?: TaskItem) => shouldBlockAppleReminderDnD(task) || isAssignedTeamTask(task),
    [shouldBlockAppleReminderDnD]
  );

  const [{ isOver: isTaskOverOnCalendar }, dropTaskOnCalendar] = useDrop(() => ({
    accept: 'TASK',
    hover: (item: TaskItem) => {
      if (shouldBlockTaskDnD(item)) {
        setDraggedTask(undefined);
        draggedTaskRef.current = undefined;
        return;
      }

      const taskWithDuration = {
        ...item,
        duration: {
          amount: item.duration?.amount || DEFAULT_TASK_DURATION_MIN,
          unit: item.duration?.unit || 'minute',
        },
      };
      setDraggedTask(taskWithDuration);
      draggedTaskRef.current = taskWithDuration;
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
    drop: (item: TaskItem) => {
      if (isAssignedTeamTask(item)) {
        setEventsSnackbar({ message: "Team tasks can't be scheduled on the calendar yet", type: 'warning', icon: 'task' });
      }
      setDraggedTask(undefined);
    },
  }));

  // Convert dragged task to event
  useEffect(() => {
    if (!draggedTask) {
      return;
    }

    const isSameEvent = (event1: CalendarEvent, event2: CalendarEvent) => {
      return (
        event1?.summary === event2?.summary && event1?.taskFrom?.taskId === event2?.taskFrom?.taskId
      );
    };

    const eventFromTask = convertTaskToEvent(draggedTask);
    const calendarColor = mainCalendar?.color || primaryColor;

    const eventWithCalendar = {
      ...eventFromTask,
      calendarId: mainCalendar?.calendarId,
      color: calendarColor,
    };

    setDraggedNewEvent((prev) => {
      if (isSameEvent(prev, eventWithCalendar)) {
        return prev;
      }
      return eventWithCalendar;
    });
  }, [draggedTask, mainCalendar]);

  const handleDropEventFromOutside = useCallback(
    debounce((data: DragFromOutsideItemArgs) => {
      const currentDraggedTask = draggedTaskRef.current; // get the task from the ref
      if (!currentDraggedTask || shouldBlockTaskDnD(currentDraggedTask)) {
        if (isAssignedTeamTask(currentDraggedTask)) {
          setEventsSnackbar({ message: "Team tasks can't be scheduled on the calendar yet", type: 'warning', icon: 'task' });
        }
        setDraggedNewEvent(undefined);
        setDraggedTask(undefined);
        draggedTaskRef.current = undefined;
        return;
      }

      const color = mainCalendar?.color || primaryColor;
      const eventFromTask = convertTaskToEvent(currentDraggedTask);

      const duration = currentDraggedTask?.duration?.amount || DEFAULT_TASK_DURATION_MIN;
      // If task has duration, add duration to start date
      const endDate = dayjs(data.start).add(duration, 'minute').toISOString();

      const newEvent = {
        ...eventFromTask,
        start: {
          date: dayjs(data.start).toISOString(),
          timeZone: dayjs.tz.guess(),
        },
        end: {
          date: endDate,
          timeZone: dayjs.tz.guess(),
        },
        color,
        scheduleFrom: 'todo',
        calendarId: mainCalendar?.calendarId,
      } as CalendarEvent;

      setDraggedNewEvent(newEvent);

      const newEventId = uuidv4().replace(/-/g, '');
      const prevDate = getDueDateYYYYMMDD(currentDraggedTask.dueDateTime);
      const newDate = dayjs(newEvent.end.date).format('YYYY-MM-DD');

      createCalendarEvent({
        newEventOption: {
          ...newEvent,
          eventId: newEventId,
          unSyncToCalendar: !!calendarPreferences.unSyncToCalendar?.dragAndDrop,
        },
        ...(prevDate && newDate && prevDate !== newDate
          ? {
              movedTaskParam: {
                prevDate,
                newDate,
                wasOverdue: getIsOverdue(prevDate),
              },
            }
          : {}),
      });

      setDraggedNewEvent(undefined);
      setDraggedTask(undefined);
      draggedTaskRef.current = undefined;
      trackEventMixpanel('drag_n_drop_task_to_calendar', { event: newEvent.summary });
    }, 10),
    [
      createCalendarEvent,
      mainCalendar,
      user.userId,
      findPhaseForTaskTime,
      shouldBlockTaskDnD,
      calendarPreferences.unSyncToCalendar,
      setEventsSnackbar,
    ]
  );

  return {
    draggedNewEvent,
    isTaskOverOnCalendar,
    dropTaskOnCalendar,
    handleDropEventFromOutside,
  };
};
